import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/api';
import { DashboardDataRequest, DashboardDataResponse } from '../types';

interface UseDashboardDataReturn {
  data: DashboardDataResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateFilters: (filters: Partial<DashboardDataRequest['filterDashboardDataReqBO']['trendtype_filtertype']>) => void;
}

export const useDashboardData = (hubId: number = 0): UseDashboardDataReturn => {
  const [data, setData] = useState<DashboardDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DashboardDataRequest['filterDashboardDataReqBO']['trendtype_filtertype']>({
    revenue: 'weekly',
    ride: 'weekly',
    customer: 'weekly',
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload: DashboardDataRequest = {
        hubid: hubId,
        filterDashboardDataReqBO: {
          trendtype_filtertype: filters,
        },
      };

      const response = await apiService.getDashboardData(payload);
      // Handle the actual API response structure
      if (response && (response as any).data) {
        setData((response as any).data as DashboardDataResponse);
      } else {
        setData(response as DashboardDataResponse);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard data');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [hubId, filters]);

  const updateFilters = useCallback((newFilters: Partial<DashboardDataRequest['filterDashboardDataReqBO']['trendtype_filtertype']>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const refetch = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  const isMounted = useRef(false);
  useEffect(() => {
    if (isMounted.current) {
      fetchDashboardData();
    } else {
      isMounted.current = true;
    }
  }, [fetchDashboardData]);

  return {
    data,
    loading,
    error,
    refetch,
    updateFilters,
  };
};
